import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import { Touchpoint } from 'api/services/mediaPlanning/touchpoints';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import usePlanStore from '../../../../../../store/planStore';
import convertDecimalToPercentValue from '../../Countries/utils/convertDecimalToPercentValue';
import MissingMandatoryTouchpointTooltip from '../MissingMandatoryTouchpointTooltip/MissingMandatoryTouchpointTooltip';

interface AccordionSummaryContentProps {
  country: string;
  missingMandatoryTouchpoints: Record<string, Touchpoint[]>;
}

export function AccordionSummaryContent({
  country,
  missingMandatoryTouchpoints,
}: AccordionSummaryContentProps) {
  const { t } = useTranslation();
  const { countryForms, storedCountryForms } = usePlanStore();

  const digitalBudget = countryForms[country].savedCalculatedBudget;
  const offlineBudget = countryForms[country].offline?.tv?.offlineBudget;
  const countryOneReach = countryForms[country].oneReach;
  const sumForDigital = digitalBudget?.reduce(
    (acc, entry) => {
      if (!entry || !entry.budget) return acc;
      return {
        currency: entry.budget.currency,
        value: acc.value + (entry.budget.value ?? 0),
      };
    },
    { currency: digitalBudget?.[0]?.budget?.currency || '', value: 0 }
  ) || { currency: 'PLN', value: 0 };

  const digitalTechCost = digitalBudget?.reduce(
    (acc, entry) => {
      if (!entry?.techCost) return acc;

      return {
        dsp: acc.dsp + (entry.techCost.dsp ?? 0),
        adServer: acc.adServer + (entry.techCost.adServer ?? 0),
      };
    },
    { dsp: 0, adServer: 0 }
  ) || { dsp: 0, adServer: 0 };

  const sumForOffline = offlineBudget
    ?.filter((entry) => entry.touchpoint !== 'tech cost')
    ?.reduce(
      (acc, entry) => {
        if (!entry?.budget) return acc;

        return {
          currency: entry.budget.currency,
          value: acc.value + (entry.budget.value ?? 0),
        };
      },
      { currency: offlineBudget?.[0]?.budget?.currency || '', value: 0 }
    ) || { currency: '', value: 0 };

  const offlineTechCost = offlineBudget
    ?.filter((entry) => entry.touchpoint === 'tech cost')
    .map((entry) => {
      return {
        value: entry.budget.value,
        currency: entry.budget.currency,
      };
    }) || [
    {
      value: 0,
      currency: 'PLN',
    },
  ];

  const getMetaRFAvailability = (targetCountry: string) => {
    return storedCountryForms[targetCountry].availableTouchpoints?.some(
      (touchpoint) =>
        touchpoint.TouchpointName === 'META' &&
        touchpoint.touchpointFunnel.toLowerCase().includes('awareness')
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!missingMandatoryTouchpoints[country]?.length && (
          <Typography variant="body1" fontWeight={600} width="110px">
            {country}
          </Typography>
        )}
        {missingMandatoryTouchpoints[country]?.length && (
          <MissingMandatoryTouchpointTooltip
            missingTouchpoints={missingMandatoryTouchpoints[country]}
            country={country}
          />
        )}
        <Typography variant="caption" textTransform="uppercase" ml={4}>
          {t('mediaPlanning.oneReach')}:{' '}
          {country !== 'POLAND' || !getMetaRFAvailability(country)
            ? 'N/A'
            : `${convertDecimalToPercentValue(countryOneReach)}%`}
        </Typography>
      </Box>
      {getMetaRFAvailability(country) && (
        <Box display="flex">
          <Typography
            variant="caption"
            textTransform="uppercase"
            lineHeight={2}
            mr={2}
          >
            Meta status:
          </Typography>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              textTransform="uppercase"
              fontWeight={700}
            >
              RF Main:
            </Typography>
            <Stack sx={{ flexDirection: 'row' }}>
              {storedCountryForms[country].metaConfig?.reservationStatus ===
                'reserved' ||
              storedCountryForms[country].metaConfig?.reservationStatus ===
                'partially-reserved' ? (
                <DoneIcon sx={{ color: '#439A37' }} />
              ) : (
                <CloseIcon sx={{ color: '#F00F00' }} />
              )}
            </Stack>
            {storedCountryForms[country].metaConfig
              ?.isMetaPartialPredictionEnabled ? (
              <>
                <Typography
                  variant="caption"
                  textTransform="uppercase"
                  fontWeight={700}
                >
                  RF 1P:
                </Typography>
                <Stack>
                  {storedCountryForms[country].metaConfig?.reservationStatus ===
                  'reserved' ? (
                    <DoneIcon sx={{ color: '#439A37' }} />
                  ) : (
                    <CloseIcon sx={{ color: '#F00F00' }} />
                  )}
                </Stack>
              </>
            ) : (
              <Box width="62px" />
            )}
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '246px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" textTransform="uppercase">
            {`${t('mediaPlanning.totalBudget')}: `}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formatNumberWithSpaces(
              (sumForDigital.value + sumForOffline.value).toFixed(0)
            )}{' '}
            {sumForDigital.currency ?? sumForOffline.currency}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" textTransform="uppercase">
            {`${t('mediaPlanning.techCosts')}: `}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formatNumberWithSpaces(
              (
                digitalTechCost.dsp +
                digitalTechCost.adServer +
                offlineTechCost[0].value
              ).toFixed(0)
            )}{' '}
            {offlineTechCost[0].currency ?? 'PLN'}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
